/**
 * Règles d'affichage du **mode illimité** (DECISIONS §7.6, arbitrage A-25).
 *
 * Le serveur ne stocke **aucun** numéro de round : l'index de vague d'une run illimitée est
 * **global** (0…7 pour le round 1, 8…15 pour le round 2, …). Ces fonctions pures sont le seul
 * endroit où le client en déduit l'affichage — aucune valeur de progression n'en dépend, et rien
 * n'est envoyé au serveur : le round est une **lecture** de l'index, pas une donnée de jeu.
 *
 * Les fonctions tolèrent un gabarit incohérent (`vaguesParRound <= 0`) en affichant le premier
 * round et la première vague : un contenu cassé ne doit pas produire `NaN` ni `Infinity` à l'écran.
 */

/**
 * Round **1-based** d'un index de vague global : `floor(index / vaguesParRound) + 1`.
 *
 * @param indexGlobal index de la vague dans la run (0-based, global en mode illimité)
 * @param vaguesParRound nombre de vagues d'un round (taille du gabarit `niv_illimite`)
 */
export function roundDepuisIndex(indexGlobal: number, vaguesParRound: number): number {
  if (!Number.isFinite(indexGlobal) || indexGlobal < 0) return 1;
  if (!Number.isFinite(vaguesParRound) || vaguesParRound <= 0) return 1;
  return Math.floor(Math.trunc(indexGlobal) / Math.trunc(vaguesParRound)) + 1;
}

/**
 * Numéro **1-based** de la vague dans son round : `(index % vaguesParRound) + 1`.
 *
 * Le total affiché reste celui du gabarit (8) : en mode illimité le contenu ne grandit pas, seul
 * l'index de run avance.
 */
export function vagueDuRound(indexGlobal: number, vaguesParRound: number): number {
  if (!Number.isFinite(indexGlobal) || indexGlobal < 0) return 1;
  if (!Number.isFinite(vaguesParRound) || vaguesParRound <= 0) return 1;
  return (Math.trunc(indexGlobal) % Math.trunc(vaguesParRound)) + 1;
}

/**
 * `true` si la vague d'index global clôt un round (dernière vague du gabarit).
 *
 * C'est la seule condition qui déclenche l'affichage du versement : elle ne remplace jamais la
 * décision du serveur, qui renvoie lui-même `versement` — l'écran n'affiche que cette valeur.
 */
export function clotureUnRound(indexGlobal: number, vaguesParRound: number): boolean {
  if (!Number.isFinite(indexGlobal) || indexGlobal < 0) return false;
  if (!Number.isFinite(vaguesParRound) || vaguesParRound <= 0) return false;
  return (Math.trunc(indexGlobal) + 1) % Math.trunc(vaguesParRound) === 0;
}

/**
 * Secondes restantes avant le lancement forcé d'une vague (`pause_illimite_ms`, §7 pt 7).
 *
 * La durée vient du contenu publié (`balance`) : elle n'est jamais écrite en dur dans le client.
 * Une durée absente ou négative renvoie `0` — donc aucun compte à rebours, jamais un délai infini.
 */
export function secondesAvantLancement(pauseMs: number): number {
  if (!Number.isFinite(pauseMs) || pauseMs <= 0) return 0;
  return Math.ceil(pauseMs / 1000);
}
